export type RemoteRuntimeAvailability = "available" | "offline" | "unknown";

export type RemoteRuntimeConnectionFailure =
  | { readonly kind: "revoked"; readonly message: string }
  | { readonly kind: "unauthorized"; readonly message: string }
  | { readonly kind: "not-enabled"; readonly message: string }
  | { readonly kind: "failed"; readonly message: string };

export type RemoteRuntimeConnectionState =
  | { readonly kind: "idle" }
  | { readonly kind: "checking" }
  | { readonly kind: "connecting" }
  | { readonly kind: "retrying" }
  | { readonly kind: "live" }
  | { readonly kind: "offline" }
  | RemoteRuntimeConnectionFailure;

export interface RemoteRuntimeConnectionAttemptState {
  readonly targetKey: string;
  readonly attempt: number;
  readonly failure: RemoteRuntimeConnectionFailure | null;
}

export interface RemoteRuntimeConnectionObservation {
  readonly connected: boolean;
  readonly effectiveAttempt: number;
  readonly failure: RemoteRuntimeConnectionFailure | null;
  readonly transportRetrying: boolean;
}

interface RemoteRuntimeConnectionErrorShape {
  readonly code?: unknown;
  readonly kind?: unknown;
  readonly message?: unknown;
  readonly status?: unknown;
}

const REVOKED_ERROR_CODES = new Set([
  "FORGE_CONTROL_DEVICE_REVOKED",
  "DEVICE_REVOKED",
  "RUNTIME_REVOKED",
  "RELAY_DEVICE_REVOKED",
  "RELAY_TICKET_REVOKED",
]);

const UNAUTHORIZED_ERROR_CODES = new Set([
  "FORGE_CONTROL_DEVICE_UNAUTHORIZED",
  "ORGANIZATION_ACCESS_DENIED",
  "PROJECT_ACCESS_DENIED",
  "SCOPE_DENIED",
  "RELAY_SCOPE_DENIED",
  "RELAY_TICKET_INVALID",
  "UNAUTHORIZED",
]);

function errorShape(error: unknown): RemoteRuntimeConnectionErrorShape {
  return typeof error === "object" && error !== null
    ? (error as RemoteRuntimeConnectionErrorShape)
    : {};
}

export function classifyRemoteRuntimeConnectionError(
  error: unknown,
): RemoteRuntimeConnectionFailure {
  const shape = errorShape(error);
  const code = typeof shape.code === "string" ? shape.code : null;
  if (code !== null && REVOKED_ERROR_CODES.has(code)) {
    return {
      kind: "revoked",
      message: "This device no longer has access to the Environment.",
    };
  }
  if (code === "FORGE_REMOTE_CONTROL_NOT_ENABLED") {
    return {
      kind: "not-enabled",
      message:
        "Remote control is not enabled for this CMMD account. Synced runtime inventory remains available.",
    };
  }
  if (
    (code !== null && UNAUTHORIZED_ERROR_CODES.has(code)) ||
    shape.kind === "auth" ||
    shape.kind === "permission" ||
    shape.status === 401 ||
    shape.status === 403
  ) {
    return {
      kind: "unauthorized",
      message: "This Forge session is not authorized to control the Environment.",
    };
  }
  return {
    kind: "failed",
    message:
      typeof shape.message === "string" && shape.message.trim().length > 0
        ? shape.message
        : "Forge could not establish a secure connection to this Environment.",
  };
}

export function deriveRemoteRuntimeConnectionState(input: {
  readonly availability: RemoteRuntimeAvailability;
  readonly connected: boolean;
  readonly attempt: number;
  readonly failure: RemoteRuntimeConnectionFailure | null;
}): RemoteRuntimeConnectionState {
  if (input.availability === "offline") return { kind: "offline" };
  if (input.availability === "unknown") return { kind: "checking" };
  if (input.connected) return { kind: "live" };
  if (input.failure !== null) return input.failure;
  return input.attempt > 0 ? { kind: "retrying" } : { kind: "connecting" };
}

export function recordRemoteRuntimeConnectionFailure(input: {
  readonly current: RemoteRuntimeConnectionAttemptState;
  readonly activeTargetKey: string;
  readonly activeAttempt: number;
  readonly failure: RemoteRuntimeConnectionFailure;
}): RemoteRuntimeConnectionAttemptState {
  if (
    input.current.targetKey === input.activeTargetKey &&
    input.current.attempt !== input.activeAttempt
  ) {
    return input.current;
  }
  return {
    targetKey: input.activeTargetKey,
    attempt: input.activeAttempt,
    failure: input.failure,
  };
}

export function observeRemoteRuntimeConnection(input: {
  readonly attemptState: RemoteRuntimeConnectionAttemptState;
  readonly targetKey: string;
  readonly hasRegisteredConnection: boolean;
  readonly transportState: "connected" | "connecting" | "disconnected" | "error" | null;
  readonly transportError: string | null;
}): RemoteRuntimeConnectionObservation {
  const effectiveAttempt =
    input.attemptState.targetKey === input.targetKey ? input.attemptState.attempt : 0;
  const attemptFailure =
    input.attemptState.targetKey === input.targetKey ? input.attemptState.failure : null;
  const transportFailure =
    input.transportState === "error" && input.transportError
      ? classifyRemoteRuntimeConnectionError(new Error(input.transportError))
      : null;
  return {
    connected: input.hasRegisteredConnection && input.transportState === "connected",
    effectiveAttempt,
    failure: attemptFailure ?? transportFailure,
    transportRetrying:
      input.hasRegisteredConnection &&
      (input.transportState === "connecting" || input.transportState === "disconnected"),
  };
}

export function recordRemoteRuntimeConnectionRetry(input: {
  readonly current: RemoteRuntimeConnectionAttemptState;
  readonly targetKey: string;
}): RemoteRuntimeConnectionAttemptState {
  return {
    targetKey: input.targetKey,
    attempt: input.current.targetKey === input.targetKey ? input.current.attempt + 1 : 1,
    failure: null,
  };
}
